import {useEffect, useState, useSyncExternalStore} from 'react';
import {Board} from './black_hole_game';

function subscribeToMotion(onChange: () => void) {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
}
const readMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A board-bound clock: replacement positions never inherit an old tally. */
export function useEndingProgress(board: Board) {
    const reducedMotion = useSyncExternalStore(subscribeToMotion, readMotion, () => true);
    const [position, setPosition] = useState({board, revision: 0});
    const revision = position.board === board ? position.revision : position.revision + 1;
    if (position.board !== board) setPosition({board, revision});
    const [clock, setClock] = useState<{board: Board, revision: number, elapsed: number} | null>(null);
    const gameOver = board.isGameOver();
    useEffect(() => {
        if (!gameOver || reducedMotion) return;
        const started = performance.now();
        const timer = window.setInterval(() => {
            const elapsed = performance.now() - started;
            setClock({board, revision, elapsed});
            if (elapsed >= 2500) window.clearInterval(timer);
        }, 40);
        return () => window.clearInterval(timer);
    }, [board, gameOver, reducedMotion, revision]);
    if (!gameOver || reducedMotion) return 1;
    const elapsed = clock?.board === board && clock.revision === revision ? clock.elapsed : 0;
    return Math.max(0, Math.min(1, (elapsed - 1300) / 1200));
}

/** Only a single forward placement may light up newly protected pieces. */
export function useNewlySafeCells(board: Board): ReadonlySet<string> {
    const [previous, setPrevious] = useState<{board: Board, cells: ReadonlySet<string>}>(() => ({board, cells: new Set()}));
    if (previous.board !== board) {
        const cells = new Set<string>();
        let forward = board.rows === previous.board.rows && board.filled === previous.board.filled + 1;
        let changes = 0;
        if (forward) {
            for (let row = 0; row < board.rows; row++) {
                for (let col = 0; col <= row; col++) {
                    const before = previous.board.at(row, col);
                    const after = board.at(row, col);
                    if (before !== after) {
                        changes++;
                        if (before !== 0 || after !== previous.board.nextMove()) forward = false;
                    }
                }
            }
        }
        if (forward && changes === 1) {
            for (let row = 0; row < board.rows; row++) {
                for (let col = 0; col <= row; col++) {
                    if (board.isSafe(row, col) && !previous.board.isSafe(row, col)) cells.add(`${row}-${col}`);
                }
            }
        }
        setPrevious({board, cells});
        return cells;
    }
    return previous.cells;
}
